import { useContext, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/card';

import { mdiContentCopy, mdiInformation } from '@mdi/js';
import Icon from '@mdi/react';
import iziToast from 'izitoast';
import { UploadIcon } from 'lucide-react';
import { useParams } from 'react-router-dom';
import { useTranslate } from 'sbx-crm-menu-ui';
import Permission from 'src/components/shared/Permission';
import { Checkbox } from 'src/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from 'src/components/ui/popover';
import useMemoAsync from 'src/hooks/useMemoAsync';
import { AI_AGENT_V2_EDIT } from 'src/types/Permission';
import { defaultValuesTextBotV3 } from 'src/utils/botGenerator/formatScript2';
import { AppAgentContext } from '../../../../context/ContextProvider';
import { upsertAgentServiceV3 } from '../../../../services/AgentService';
import { appActions, State } from '../../../../store';
import { ModalType } from '../../../../types/Modal';
import { copyToClipboard, generateJsBotFileV3, grantPermission } from '../../../../utils';
import ButtonComponent from '../../../shared/ButtonComponent';
import { Switch } from '../../../ui/switch';
import { valueAttachmentDefault } from '../../bot-script/BotScript';
import { AgentSetupContextV3 } from '../AgentSetupV3Component';

export interface OptionsUploadScript {
    assessor: number | string;
    withWhatsApp: boolean;
    defaultCountry?: string;
    withMicrophone: boolean;
    withAssessor: boolean;
    withAttachment: { pdf: boolean; image: boolean; excel: boolean; text: boolean; word: boolean; csv: boolean };
    withInitialMessage: boolean;
}

const AgentDeployV3Component = () => {
    const { t: agent } = useTranslate('agent');
    const { t: c } = useTranslate('common');

    const {
        state: {
            session: { ai_agent_config, origin, domainName, permissions },
        },
        dispatch,
    } = useContext(AppAgentContext);
    const { id } = useParams();
    const { getValues, setValue, watch } = useContext(AgentSetupContextV3);
    const [activeAttachment, setActiveAttachment] = useState(
        Object.values(
            getValues().look_and_feel_chat?.extra_config?.config_bot?.withAttachment || valueAttachmentDefault
        ).some((v) => v)
    );
    const [refreshCount, setRefreshCount] = useState(0);
    const [isSameVersion, setIsSameVersion] = useState(true);
    const [versionBot, setVersionBot] = useState('');

    const allowEdit = useMemo(() => {
        return grantPermission(AI_AGENT_V2_EDIT, permissions);
    }, [permissions]);

    const generateBot = (
        microphone: boolean,
        withAssessor: boolean,
        attachment: { pdf: boolean; image: boolean; excel: boolean; text: boolean; word: boolean },
        initialMessage: boolean
    ) => {
        dispatch(appActions.closeModal(ModalType.DYNAMIC));
        if (id) {
            generateJsBotFileV3(
                origin.concat('/www/', domainName, '/ai-agent/'),
                id || '',
                ai_agent_config?.folders?.css ?? '',
                ai_agent_config?.folders?.scripts ?? '',
                microphone,
                withAssessor,
                attachment,
                initialMessage,
                (state) => {
                    switch (state) {
                        case State.LOADING:
                            iziToast.info({
                                message: `${agent('generating_script')}...`,
                                position: 'bottomLeft',
                            });
                            break;
                        case State.SUCCESS:
                            iziToast.success({
                                message: `${agent('script_generated_successfully')}.`,
                                position: 'bottomLeft',
                            });
                            break;
                        case State.ERROR:
                            iziToast.warning({
                                message: `${agent('script_generation_failed')}`,
                                position: 'bottomLeft',
                            });
                            break;
                    }
                }
            ).then(() => {
                setRefreshCount(refreshCount + 1);
            });
        }
    };

    const existUrl = useMemoAsync(
        async () => {
            try {
                const res = await fetch(
                    origin.concat('/www/', domainName, '/ai-agent/scripts/', getValues('name'), 'v3', '.js')
                );
                const res2 = await res.text();
                const regex = /(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})/;

                const match = res2.match(regex);

                if (match) {
                    const isSame = match[1] === getValues('look_and_feel_chat.extra_config.version');
                    setIsSameVersion(isSame);
                    setVersionBot(match[1]);
                } else {
                    setIsSameVersion(false);
                }
                return res.status === 200;
            } catch (error) {
                return false;
            }
        },
        [refreshCount],
        false
    );

    function updatedConfigBot(values: OptionsUploadScript) {
        let submittedData;

        if (!getValues().look_and_feel_chat?.extra_config) {
            submittedData = {
                ...getValues(),
                look_and_feel_chat: {
                    ...getValues().look_and_feel_chat,
                    extra_config: JSON.stringify({
                        ...defaultValuesTextBotV3,
                        config_bot: values,
                    }),
                },
            };
        } else {
            submittedData = {
                ...getValues(),
                look_and_feel_chat: {
                    ...getValues().look_and_feel_chat,
                    extra_config: JSON.stringify({
                        ...getValues().look_and_feel_chat.extra_config,
                        config_bot: values,
                    }),
                },
            };
        }

        if (id && id !== 'create') {
            submittedData.id = parseInt(id);
        }
        upsertAgentServiceV3(submittedData).then();
    }

    return (
        <Card className="bg-gradient-to-br from-purple-100 to-pink-100 shadow-xl mb-3">
            <CardHeader className="flex  md:flex-row md:items-center justify-between">
                <CardTitle className="text-2xl font-bold text-purple-800 flex items-center gap-2">
                    {agent('upload_script')}{' '}
                    {versionBot && (
                        <span className="text-xs text-primary">
                            (version: {versionBot})
                        </span>
                    )}
                </CardTitle>
                <div className="flex items-center gap-2">
                    {existUrl && (
                        <ButtonComponent
                            title={c('copy_url_script')}
                            onClick={() =>
                                copyToClipboard(
                                    origin.concat(
                                        '/www/',
                                        domainName,
                                        '/ai-agent/scripts/',
                                        getValues('name'),
                                        'v3',
                                        '.js'
                                    ),
                                    c('copied_to_clipboard')
                                )
                            }
                            icon={<Icon className="text-white" size={1} path={mdiContentCopy} />}
                            className="primary-gradient-button"
                        />
                    )}
                    {!isSameVersion && (
                        <Permission permission={AI_AGENT_V2_EDIT}>
                            <ButtonComponent
                                label={agent('upload_script')}
                                icon={<UploadIcon />}
                                className="secondary-gradient-button"
                                disabled={
                                    !watch().look_and_feel_chat?.extra_config?.config_bot ||
                                    (Object.values(
                                        watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment || {
                                            ...valueAttachmentDefault,
                                        }
                                    ).every((v) => !v) &&
                                        activeAttachment)
                                }
                                onClick={() => {
                                    const dd = watch().look_and_feel_chat?.extra_config?.config_bot;
                                    const state = dd || {
                                        assessor: undefined,
                                        withWhatsApp: false,
                                        withMicrophone: false,
                                        withAssessor: true,
                                        withAttachment: valueAttachmentDefault,
                                        withInitialMessage: true,
                                    };
                                    updatedConfigBot(state);
                                    generateBot(
                                        false,
                                        state.withAssessor,
                                        state.withAttachment,
                                        state.withInitialMessage
                                    );
                                }}
                            />
                        </Permission>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-4">
                <div className="px-3">
                    <div className="">
                        <ActiveOptionAgentV3
                            label={agent('microphone')}
                            description={agent('active_microphone_bot')}
                            value={watch().look_and_feel_chat?.extra_config?.config_bot?.withMicrophone}
                            setValue={(val) =>
                                setValue('look_and_feel_chat.extra_config.config_bot.withMicrophone', val)
                            }
                            disabled={id !== 'create' && !allowEdit}
                        />
                        <ActiveOptionAgentV3
                            label={agent('assessor')}
                            description={agent('active_assessor_bot')}
                            value={watch().look_and_feel_chat?.extra_config?.config_bot?.withAssessor}
                            setValue={(val) => setValue('look_and_feel_chat.extra_config.config_bot.withAssessor', val)}
                            disabled={id !== 'create' && !allowEdit}
                        />
                        <ActiveOptionAgentV3
                            label={agent('attachment')}
                            description={agent('active_attachment_bot')}
                            value={activeAttachment}
                            setValue={(val) => {
                                if (!val) {
                                    setValue(
                                        'look_and_feel_chat.extra_config.config_bot.withAttachment',
                                        valueAttachmentDefault
                                    );
                                }
                                setActiveAttachment(val);
                            }}
                            disabled={id !== 'create' && !allowEdit}
                        />
                        {activeAttachment && (
                            <div className="grid grid-cols-3 gap-4 mt-2">
                                <div className="flex items-center gap-1">
                                    <Checkbox
                                        id="images"
                                        onClick={() =>
                                            setValue(
                                                'look_and_feel_chat.extra_config.config_bot.withAttachment.image',
                                                !watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.image
                                            )
                                        }
                                        checked={
                                            watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment?.image
                                        }
                                        disabled={id !== 'create' && !allowEdit}
                                    />
                                    <label htmlFor="images" className="text-sm">
                                        Images
                                    </label>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Checkbox
                                        id="pdf-file"
                                        onClick={() =>
                                            setValue(
                                                'look_and_feel_chat.extra_config.config_bot.withAttachment.pdf',
                                                !watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.pdf
                                            )
                                        }
                                        checked={
                                            watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment?.pdf
                                        }
                                        disabled={id !== 'create' && !allowEdit}
                                    />
                                    <label htmlFor="pdf-file" className="text-sm">
                                        PDF
                                    </label>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Checkbox
                                        id="excel-file"
                                        onClick={() =>
                                            setValue(
                                                'look_and_feel_chat.extra_config.config_bot.withAttachment.excel',
                                                !getValues().look_and_feel_chat?.extra_config?.config_bot
                                                    ?.withAttachment?.excel
                                            )
                                        }
                                        checked={
                                            watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment?.excel
                                        }
                                        disabled={id !== 'create' && !allowEdit}
                                    />
                                    <label htmlFor="excel-file" className="text-sm">
                                        Excel
                                    </label>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Checkbox
                                        id="csv-file"
                                        onClick={() =>
                                            setValue(
                                                'look_and_feel_chat.extra_config.config_bot.withAttachment.csv',
                                                !watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.csv
                                            )
                                        }
                                        checked={
                                            watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment?.csv
                                        }
                                        disabled={id !== 'create' && !allowEdit}
                                    />
                                    <label htmlFor="csv-file" className="text-sm">
                                        CSV
                                    </label>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Checkbox
                                        id="text-file"
                                        onClick={() =>
                                            setValue(
                                                'look_and_feel_chat.extra_config.config_bot.withAttachment.text',
                                                !watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.text
                                            )
                                        }
                                        checked={
                                            watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment?.text
                                        }
                                        disabled={id !== 'create' && !allowEdit}
                                    />
                                    <label htmlFor="text-file" className="text-sm">
                                        Text
                                    </label>
                                </div>
                                <div className="flex items-center gap-1">
                                    <Checkbox
                                        id="word-file"
                                        onClick={() =>
                                            setValue(
                                                'look_and_feel_chat.extra_config.config_bot.withAttachment.word',
                                                !watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment
                                                    ?.word
                                            )
                                        }
                                        checked={
                                            watch().look_and_feel_chat?.extra_config?.config_bot?.withAttachment?.word
                                        }
                                        disabled={id !== 'create' && !allowEdit}
                                    />
                                    <label htmlFor="word-file" className="text-sm">
                                        Word
                                    </label>
                                </div>
                            </div>
                        )}
                        <ActiveOptionAgentV3
                            label={agent('initial_message')}
                            description={agent('active_initial_message_bot')}
                            value={watch().look_and_feel_chat?.extra_config?.config_bot?.withInitialMessage}
                            setValue={(val) =>
                                setValue('look_and_feel_chat.extra_config.config_bot.withInitialMessage', val)
                            }
                            disabled={id !== 'create' && !allowEdit}
                        />
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};

export default AgentDeployV3Component;

export function ActiveOptionAgentV3({
    value,
    setValue,
    label,
    description,
    disabled,
}: {
    value: boolean;
    setValue: (val: boolean) => void;
    label: string;
    description: string;
    disabled?: boolean;
}) {
    return (
        <div className="flex justify-between items-center mt-4">
            <div className="flex items-center gap-2">
                <label htmlFor="">{label}</label>
                <Popover>
                    <PopoverTrigger>
                        <div className="rounded-full bg-black">
                            <Icon path={mdiInformation} size={0.7} className="text-white fill-white" />
                        </div>
                    </PopoverTrigger>
                    <PopoverContent>
                        <div>
                            <p className="text-sm text-center">{description}</p>
                        </div>
                    </PopoverContent>
                </Popover>
            </div>
            <Switch
                className={`data-[state=checked]:bg-primary data-[state=unchecked]:bg-gray-200 scale-90`}
                checked={value}
                disabled={disabled}
                onClick={() => {
                    setValue(!value);
                }}
            />
        </div>
    );
}
